import React, { useState, useEffect } from "react";
import {
  Box,
  useTheme,
  TableRow,
  Typography,
  MenuItem,
  Stack,
  TextField,
  Paper,
  TableCell,
  Table,
  TableHead,
  TableBody,
} from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import { IAsset, IOrder } from "@/interfaces";
import Store from "@/store";
import { inject, observer } from "mobx-react";
import { OrderStore } from "@/store/orders";
import { autorun } from "mobx";
import { useNavigate } from "react-router-dom";
import ManageOrder from "@/components/Order/ManageOrder";
import { ORDER_STATUS } from "@/@types/EnumType";
import { formatedBigNumber } from "@/utility";
import CommonStore from "@/store/common";
import { ORDER_TYPE } from "@/@types/EnumType";
import { ChatOrderStore } from "@/store/chat_orders";
import IChatOrder from "@/interfaces/chatOrder";

const MyOrderPage = inject(
  Store.type.orderStore,
  Store.type.commonStore,
  Store.type.chatOrderStore,
)(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  observer((props: any) => {
    const orderStore = props[Store.type.orderStore] as OrderStore;
    const commonStore = props[Store.type.commonStore] as CommonStore;
    const chatOrderStore = props[Store.type.chatOrderStore] as ChatOrderStore;

    const navigate = useNavigate();
    const theme = useTheme();

    const [orderList, setOrderList] = useState<IOrder[]>([]);
    const [filteredOrderList, setFilteredOrderList] = useState<IOrder[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);

    const [assetList, setAssetList] = useState<IAsset[]>([]);
    const [asset, setAsset] = useState<string>("null");
    const [status, setStatus] = useState<ORDER_STATUS | "null">("null");
    const [type, setType] = useState<ORDER_TYPE | "null">("null");

    useEffect(() => {
      commonStore.fetchAssets().catch((error) => {
        console.error("Error assets:", error);
      });
      const disposeAutorun = autorun(() => {
        const assets = commonStore.assets;
        if (assets) {
          setAssetList(assets);
        }
      });
      return () => {
        disposeAutorun();
      };
    }, [commonStore]);

    useEffect(() => {
      const disposeAutorun = autorun(() => {
        const orders = orderStore.myOrders;
        const AllMessages = chatOrderStore.myOrderMessages;
        if (AllMessages) {
          const messages = Object.values(
            AllMessages.reduce(
              (acc: Record<string, IChatOrder>, message: IChatOrder) => {
                if (
                  !acc[message.order_id] ||
                  acc[message.order_id].created_at < message.created_at
                ) {
                  acc[message.order_id] = message;
                }
                return acc;
              },
              {},
            ),
          );
          if (orders && messages) {
            const combinedOrders = orders.map((order) => {
              const latestMessage = messages.find(
                (message) => message.order_id === order.order_id,
              );

              return {
                ...order,
                latest_message: latestMessage?.created_at || undefined,
              };
            });

            const sortedOrders = combinedOrders.sort((a, b) =>
              (b.latest_message || b.created_at || "").localeCompare(
                a.latest_message || a.created_at || "",
              ),
            );
            setOrderList(sortedOrders);
          }
        }
      });

      return () => disposeAutorun();
    }, [orderStore, chatOrderStore]);

    useEffect(() => {
      let myOrders = orderList;
      if (myOrders) {
        if (asset !== "null") {
          myOrders = myOrders.filter((order) => order.asset.asset_id === asset);
        }
        if (status !== "null") {
          myOrders = myOrders.filter((order) => order.status === status);
        }
        if (type !== "null") {
          myOrders = myOrders.filter((order) => order.order_type === type);
        }
        setFilteredOrderList(myOrders);
      }
    }, [orderList, asset, status, type]);

    return (
      <>
        <Paper
          sx={{
            m: "82.5px 78px 50.5px 56px",
            p: "68px 54px 50px 64px",
            minHeight: `calc((100vh - ${theme.navbarHeight} - ${theme.footerHeight} - 82.5px - 50.5px))`,
          }}
        >
          <Typography variant="h3" textAlign="center" mb="80px">
            Orders
          </Typography>
          <Stack
            direction="row"
            gap="33.3px"
            mb="71.13px"
            sx={{ float: "right" }}
          >
            <TextField
              select={true}
              label="Order status"
              type="text"
              name="status"
              id="status"
              value={status === "null" ? "null" : status}
              onChange={(e) => {
                const newValue = e.target.value;
                if (
                  newValue === ORDER_STATUS.OPEN ||
                  newValue === ORDER_STATUS.CLOSED ||
                  newValue === ORDER_STATUS.CANCELLED ||
                  newValue === "null"
                ) {
                  setStatus(newValue);
                }
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: "none !important",
                  },
              }}
            >
              <MenuItem value={"null"}>All</MenuItem>
              <MenuItem
                value={ORDER_STATUS.OPEN}
                sx={{
                  color: theme.colors.green,
                }}
              >
                OPEN
              </MenuItem>
              <MenuItem
                value={ORDER_STATUS.CLOSED}
                sx={{
                  color: theme.colors.darkFontColor,
                }}
              >
                CLOSED
              </MenuItem>
              <MenuItem
                value={ORDER_STATUS.CANCELLED}
                sx={{
                  color: theme.colors.red,
                }}
              >
                CANCELLED
              </MenuItem>
            </TextField>
            <TextField
              select={true}
              label="Asset"
              type="text"
              name="asset"
              id="asset"
              value={asset === "null" ? "null" : asset}
              onChange={(e) => setAsset(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: "none !important",
                  },
              }}
            >
              <MenuItem value={"null"}>All</MenuItem>
              {assetList.map((pm) => (
                <MenuItem key={pm.asset_id} value={pm.asset_id}>
                  {pm.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select={true}
              label="Order type"
              type="text"
              name="type"
              id="type"
              value={type === "null" ? "null" : type}
              onChange={(e) => {
                const newValue = e.target.value;
                if (
                  newValue === "BUY" ||
                  newValue === "SELL" ||
                  newValue === "null"
                ) {
                  setType(newValue);
                }
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline, .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: "none !important",
                  },
              }}
            >
              <MenuItem value={"null"}>All</MenuItem>
              <MenuItem
                value="SELL"
                sx={{
                  color: theme.colors.red,
                }}
              >
                SELL ORDER
              </MenuItem>
              <MenuItem
                value="BUY"
                sx={{
                  color: theme.colors.green,
                }}
              >
                BUY ORDER
              </MenuItem>
            </TextField>
          </Stack>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "7.5%" }}>Order Type</TableCell>
                <TableCell style={{ width: "14.5%" }}>Asset</TableCell>
                <TableCell style={{ width: "14.5%" }}>Store</TableCell>
                <TableCell style={{ width: "7.5%" }}>Currency</TableCell>
                <TableCell style={{ width: "16.5%" }}>Price</TableCell>
                <TableCell style={{ width: "16.5%" }}>Quantity</TableCell>
                <TableCell style={{ width: "12.5%" }}>Payment method</TableCell>
                <TableCell style={{ width: "12.5%" }}>Latest message</TableCell>
                <TableCell style={{ width: "12.5%" }}>Status</TableCell>
                <TableCell style={{ width: "10.5%" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrderList.length > 0 &&
                filteredOrderList.map((row) => (
                  <TableRow key={row.order_id}>
                    <TableCell
                      sx={{
                        color:
                          row.order_type === "SELL"
                            ? `${theme.colors.red} !important`
                            : `${theme.colors.green} !important`,
                        fontWeight: "bold !important",
                      }}
                    >
                      {row.order_type}
                    </TableCell>
                    <TableCell>{row.asset.name}</TableCell>
                    <TableCell>{row.post.title}</TableCell>
                    <TableCell>{row.currency}</TableCell>
                    <TableCell
                      style={{
                        fontSize: "14px",
                      }}
                    >
                      {formatedBigNumber(row.price)} {row.currency}
                    </TableCell>
                    <TableCell>{`${formatedBigNumber(row.order_amount)} ${
                      row.asset.symbol
                    }`}</TableCell>
                    <TableCell>
                      {row.payment.payment_method.payment_name}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        if (row.latest_message) {
                          const date = new Date(row.latest_message);
                          return `${date.toLocaleDateString()} ${date.toLocaleTimeString(
                            [],
                            { hour: "numeric", minute: "numeric" },
                          )}`;
                        } else {
                          return "-";
                        }
                      })()}
                    </TableCell>
                    <TableCell
                      sx={{
                        color:
                          row.status === ORDER_STATUS.OPEN
                            ? `${theme.colors.green} !important`
                            : row.status === ORDER_STATUS.CANCELLED
                            ? `${theme.colors.red} !important`
                            : `${theme.colors.darkFontColor} !important`,
                      }}
                    >
                      {row.status}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="table"
                        color="primary"
                        size="small"
                        onClick={() => setSelectedOrder(row)}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {!filteredOrderList.length && (
            <>
              <Typography
                variant="h3"
                color={theme.colors.noFound}
                mt="82.24px"
                textAlign="center"
              >
                You haven&apos;t made any trades yet.
              </Typography>
              <Box width="fit-content" mt="59px" mb="69px" mx="auto">
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={() => {
                    navigate("/trade");
                  }}
                >
                  Find a trade offer
                </Button>
              </Box>
            </>
          )}
        </Paper>
        {selectedOrder && (
          <ManageOrder
            selectedOrder={selectedOrder}
            setSelectedOrder={setSelectedOrder}
          />
        )}
      </>
    );
  }),
);

export default MyOrderPage;
